import { Analytics } from '@cobliteam/analytics';
import type { RiskRecognitionHighlight } from '@cobliteam/api-lib/src/dashboard/riskRecognitions/Types';
import { useInvalidateRiskRecognitionList, useRiskRecognitionHighlight } from '@cobliteam/hooks/src/useEntity/useRiskRecognitions';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { dismissToast, showCardToast } from '../../../core/showCardToast';
import useNotificationDrawerStore from '../../../core/store/useNotificationDrawerStore';
import { buildRiskRecognitionEventProps, RISK_RECOGNITION_EVENTS } from '../analytics';
import { isHighlightRenderable, RISK_DRAWER_PARAM } from '../parse';
import RiskCardBody from '../RiskCardBody';

const RISK_RECOGNITION_TYPE_KEY = 'risk-recognition';
// Id fixo: reusar o mesmo toast faz cada novo evento substituir o anterior — só o último fica na tela.
const RISK_RECOGNITION_TOAST_ID = 'risk-recognition-toast';
// Janela de recência: só toasta evento que aconteceu nos últimos 10 min (horário local). Sem isso, um
// arquivar/desarquivar (que invalida e reexpõe outro highlight ativo, possivelmente antigo) dispararia toast.
const RECENT_TOAST_WINDOW_MS = 10 * 60 * 1000;

// debug=true na URL: toasta o último highlight na primeira carga e ignora a janela de recência (10 min).
// Pra testar o card sem esperar um evento fresco. Lido no momento da chamada (sem reatividade a navegação).
const isDebugMode = (): boolean => new URLSearchParams(window.location.search).get('debug') === 'true';

const isRecentEvent = (highlight: RiskRecognitionHighlight): boolean => {
  if (isDebugMode()) return true;
  const createdMs = Date.parse(highlight.created_at);
  if (!Number.isFinite(createdMs)) return false;
  const ageMs = Date.now() - createdMs;
  return ageMs >= 0 && ageMs <= RECENT_TOAST_WINDOW_MS;
};

// Gate de exibição: highlight precisa ser renderável e ter metadata pra virar card.
const canToast = (highlight: RiskRecognitionHighlight): boolean => isHighlightRenderable(highlight, null) && Boolean(highlight.metadata);

// Dispara o toast do risco (mesmo corpo do card flutuante, via helper do sonner). Labels caem no i18n do
// RiskCardBody. Extraído do effect pra manter a complexidade baixa.
const showRiskToast = (highlight: RiskRecognitionHighlight, history: ReturnType<typeof useHistory>) => {
  Analytics.track(RISK_RECOGNITION_EVENTS.displayed, buildRiskRecognitionEventProps(highlight, 'toast'));

  showCardToast(RISK_RECOGNITION_TOAST_ID, (toastId) => (
    <RiskCardBody
      highlight={highlight}
      className='w-[352px] max-w-[calc(100vw-2rem)]'
      onDismiss={() => {
        Analytics.track(RISK_RECOGNITION_EVENTS.dismissed, buildRiskRecognitionEventProps(highlight, 'toast'));
        dismissToast(toastId);
      }}
      onAction={() => {
        Analytics.track(RISK_RECOGNITION_EVENTS.action, buildRiskRecognitionEventProps(highlight, 'toast'));
        // Reflete o deep link na URL (igual ao card) pra reload/compartilhamento manterem o evento aberto.
        const params = new URLSearchParams(history.location.search);
        params.set(RISK_DRAWER_PARAM, highlight.id);
        history.push({ search: `?${params.toString()}` });
        useNotificationDrawerStore.getState().openDrawer(RISK_RECOGNITION_TYPE_KEY, highlight);
        dismissToast(toastId);
      }}
    />
  ));
};

type ToastDedupState = { seeded: boolean; lastBucketEnd: string | null };

// Decide e dispara o toast (muta `state`: semeadura + dedup por bucket end). Extraído do effect pra manter
// a complexidade baixa. Primeira carga: semeia (não toasta histórico); debug=true toasta o atual pra testar.
// Cargas seguintes: bucket novo, renderável e recente (≤10 min) — a recência evita toast ao arquivar.
const processHighlight = (highlight: RiskRecognitionHighlight | undefined, state: ToastDedupState, history: ReturnType<typeof useHistory>, invalidateList: () => void) => {
  if (!state.seeded) {
    state.seeded = true;
    if (!highlight) return;
    state.lastBucketEnd = highlight.time_bucket_end;
    if (isDebugMode() && canToast(highlight)) showRiskToast(highlight, history);
    return;
  }

  if (!highlight || state.lastBucketEnd === highlight.time_bucket_end) return;
  state.lastBucketEnd = highlight.time_bucket_end;
  // Bucket novo: o sino/página precisam refletir o item mesmo quando NÃO toasta (>10 min) — invalida a lista.
  invalidateList();
  if (canToast(highlight) && isRecentEvent(highlight)) showRiskToast(highlight, history);
};

/**
 * Controller da surface "toast" do risco. Notifica novos eventos em QUALQUER página (diferente do card
 * flutuante, só /monitoring). Lê o endpoint `/v1/risk-recognitions/highlights` (RiskRecognitionsRequester)
 * e dispara o toast a cada bucket NOVO. Só o último evento fica visível (id fixo, sem auto-dismiss).
 */
const useRiskToastController = () => {
  const { knockToastRiskRecognition } = useFeatureFlags();
  const { data: highlight, isSuccess } = useRiskRecognitionHighlight(knockToastRiskRecognition);
  const history = useHistory();
  const invalidateList = useInvalidateRiskRecognitionList();
  const stateRef = useRef<ToastDedupState>({ seeded: false, lastBucketEnd: null });

  useEffect(() => {
    if (isSuccess) processHighlight(highlight, stateRef.current, history, invalidateList);
  }, [highlight, isSuccess, history, invalidateList]);
};

export default useRiskToastController;
